import { CellInput, FontStyle, RowInput, Styles } from "jspdf-autotable";
import { GarageSettings } from "../common/useGarageSettings";
import { ReportPrinter } from "./ReportPrinter";
import { PageData } from "./usePrintJourneyListData";

function printDottedLine(
  printer: ReportPrinter,
  text: string | null,
  x1: number,
  x2: number,
  y: number
) {
  if (text && text !== "") {
    const tsize = printer.printTextExt(text, x1, y, "unlimited", {
      align: "left",
    });
    printer.doc.line(x1 + tsize.width + 5, y, x2, y);
  } else {
    printer.doc.line(x1, y, x2, y);
  }
}

function printTwoColumns(
  printer: ReportPrinter,
  col1: string,
  col2: string,
  col1Right: number,
  col2Left: number,
  printJoin: (x1: number, x2: number, y: number) => void
) {
  const y = printer.currentPageY;
  printer.printText(col1, col1Right, "unlimited", {
    align: "right",
  });
  printer.printTextExt(col2, col2Left, y, "unlimited", { align: "left" });
  printJoin(col1Right + 5, col2Left - 5, y);
}

function printCoursesTable(
  printer: ReportPrinter,
  printPowerConsumption: boolean,
  number: number,
  total: number
) {
  const maxWidth = (printer.printableWidth - 2 * printer.pageMarginX) / total;
  const xOffset = printer.pageMarginX * 2 + number * maxWidth;

  const startY = printer.currentPageY;

  printer.printSpace(printer.doc.getFontSize() + 5);
  const lineSpacing = printer.getLineSpacing();
  printer.setLineSpacing(1.2);

  printer.doc.setLineDashPattern([1, 3], 0);

  const col1size = printer.getDimensions("От тях по разписание");
  const col2size = printer.getDimensions("kWh");
  const col1 = xOffset + 5 + col1size.w;
  const col2 = xOffset + maxWidth - 5 - col2size.w;

  printTwoColumns(
    printer,
    "Направени курсове",
    "бр.",
    col1,
    col2,
    (x1, x2, y) => printDottedLine(printer, null, x1, x2, y)
  );
  printTwoColumns(
    printer,
    "От тях по разписание",
    "бр.",
    col1,
    col2,
    (x1, x2, y) => printDottedLine(printer, null, x1, x2, y)
  );
  printTwoColumns(printer, "Извън разписание", "бр.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, null, x1, x2, y)
  );
  printTwoColumns(printer, "в движение", "ч.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, null, x1, x2, y)
  );
  printTwoColumns(printer, "в престой", "ч.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, null, x1, x2, y)
  );

  printTwoColumns(printer, "Изработено време:", "ч.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, "в резерва", x1, x2, y)
  );
  printTwoColumns(printer, "", "ч.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, "в маневра", x1, x2, y)
  );
  printTwoColumns(printer, "", "ч.", col1, col2, (x1, x2, y) =>
    printDottedLine(printer, "нощен труд", x1, x2, y)
  );

  if (printPowerConsumption) {
    if (total > 2) {
      printTwoColumns(
        printer,
        "Електроенергия:",
        "kWh",
        col1,
        col2,
        (x1, x2, y) => printDottedLine(printer, "икономия:", x1, x2, y)
      );
      printTwoColumns(printer, "", "kWh", col1, col2, (x1, x2, y) =>
        printDottedLine(printer, "преразход:", x1, x2, y)
      );
    } else {
      printTwoColumns(
        printer,
        "Електроенергия:",
        "kWh",
        col1,
        col2,
        (x1, x2, y) => {
          const t1size = printer.getDimensions("икономия:");
          const t2size = printer.getDimensions("преразход:");
          const space = x2 - x1 - t1size.w - t2size.w;

          printer.printTextExt("икономия:", x1, y, "unlimited", {
            align: "left",
          });
          printer.printTextExt(
            "преразход:",
            x1 + t1size.w + space / 2,
            y,
            "unlimited",
            {
              align: "left",
            }
          );

          printer.doc.line(
            x1 + t1size.w + 5,
            y,
            x1 + t1size.w + space / 2 - 5,
            y
          );
          printer.doc.line(x1 + t1size.w + t2size.w + space / 2 + 5, y, x2, y);
        }
      );
    }
  }

  printer.printSpace(8);

  const currentY = printer.currentPageY;
  let tsize = printer.printText("Диспечер:", xOffset + 5);
  printer.doc.line(
    xOffset + 5 + tsize.width + 5,
    printer.currentPageY - tsize.height,
    xOffset + maxWidth / 2 - 10,
    printer.currentPageY - tsize.height
  );
  printer.withFont(
    () => {
      printer.printTextExt(
        "(подпис)",
        xOffset + maxWidth / 2 - 13,
        printer.currentPageY - 4,
        "unlimited",
        { align: "right" }
      );
    },
    "slab-light",
    8
  );

  printer.setPosition(currentY);
  tsize = printer.printText("Изчислител:", xOffset + maxWidth / 2);
  printer.doc.line(
    xOffset + maxWidth / 2 + 5 + tsize.width,
    printer.currentPageY - tsize.height,
    xOffset + maxWidth - 10,
    printer.currentPageY - tsize.height
  );
  printer.withFont(
    () => {
      printer.printTextExt(
        "(подпис)",
        xOffset + maxWidth - 10,
        printer.currentPageY - 4,
        "unlimited",
        { align: "right" }
      );
    },
    "slab-light",
    8
  );

  // printer.printSpace(5);

  const endY = printer.currentPageY;
  printer.doc.setLineDashPattern([], 0);

  printer.doc.line(xOffset, startY, xOffset, endY);
  printer.doc.line(xOffset + maxWidth, startY, xOffset + maxWidth, endY);
  printer.doc.line(xOffset, startY, xOffset + maxWidth, startY);
  printer.doc.line(xOffset, endY, xOffset + maxWidth, endY);

  printer.setLineSpacing(lineSpacing);
}

function printShiftTable(
  printer: ReportPrinter,
  printPowerConsumption: boolean,
  number: number,
  total: number
) {
  const visibleWidth =
    (printer.printableWidth - printer.pageMarginX * 2) / total;
  const startX = printer.pageMarginX * 2 + (number - 1) * visibleWidth;
  const shiftText =
    number === 1
      ? "I смяна "
      : number === 2
        ? "II смяна "
        : number === 3
          ? "III смяна "
          : "";

  const headStyles: Partial<Styles> = {
    halign: "center",
    font: "Roboto",
    fontStyle: "slab-normal" as FontStyle,
    fontSize: 8,
  };

  const header: CellInput[][] = [
    [
      {
        content: shiftText + "водач № .....................................",
        colSpan: 6,
        styles: headStyles,
      },
    ],
    [
      { content: "Печат контр.", rowSpan: 2, styles: headStyles },
      {
        content: "Време на пристигане на главни спирки",
        colSpan: 4,
        styles: headStyles,
      },
      { content: "Печат контр.", rowSpan: 2, styles: headStyles },
    ],
    [
      { content: "", styles: headStyles },
      { content: "", styles: headStyles },
      { content: "", styles: headStyles },
      { content: "", styles: headStyles },
    ],
  ];
  // const rowCount = printPowerConsumption ? 14 : 19;
  const rowCount = printPowerConsumption ? (total === 3 ? 12 : 14) : 19;
  const body: CellInput[][] = [...new Array(rowCount)].map(() => [
    " ",
    " ",
    " ",
    " ",
    " ",
    " ",
  ]);

  printer.autoTable({
    margin: {
      left: startX,
      right: printer.pageMarginX * 2,
      vertical: printer.pageMarginY * 1.5,
    },
    styles: {
      halign: "left",
      valign: "middle",
      font: "Roboto",
      fontStyle: "slab-light" as FontStyle,
      fontSize: 9,
      cellPadding: 4,
      lineColor: [0, 0, 0],
      lineWidth: 0.25,
    },
    headStyles: headStyles,
    columnStyles: {
      0: { cellWidth: printPowerConsumption ? 45 : 40 },
      1: {},
      2: {},
      3: {},
      4: {},
      5: { cellWidth: printPowerConsumption ? 45 : 40 },
    },
    columns: ["", "", "", "", "", ""],
    showHead: false,
    showFoot: false,
    tableWidth: visibleWidth,
    body: header.concat(body),
  });
}

function powerConsumptionTable(
  printer: ReportPrinter,
  number: number,
  total: number
) {
  const width = (printer.printableWidth - 2 * printer.pageMarginX) / total;
  const offset = printer.pageMarginX * 2 + number * width;

  printer.autoTable({
    showHead: true,
    margin: {
      left: offset,
      right: printer.pageMarginX * 2 + width,
      vertical: printer.pageMarginY * 1.5,
    },
    styles: {
      halign: "left",
      valign: "middle",
      font: "Roboto",
      fontStyle: "slab-light" as FontStyle,
      fontSize: 9,
      cellPadding: 4,
      lineColor: [0, 0, 0],
      lineWidth: 0.25,
    },
    headStyles: {
      halign: "center",
      font: "Roboto",
      fontStyle: "slab-normal" as FontStyle,
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: (width - 45) / 5 },
      2: { cellWidth: (width - 45) / 5 },
      3: { cellWidth: (width - 45) / 5 },
      4: { cellWidth: (width - 45) / 5 },
      5: { cellWidth: (width - 45) / 5 },
    },
    columns: [
      "",
      total > 2
        ? "Показа-\nния на\nкиломе-\nтража"
        : "Показания на\nкилометража",
      total > 2 ? "Ec\nконсу-\nмирана енергия" : "Ec\nконсумирана\nенергия",
      total > 2 ? "Er\nвърната енергия\n" : "Er\nвърната\nенергия",
      total > 2 ? "Et\nенергия за отоп-\nление" : "Et\nенергия за\nотопление",
      total > 2 ? "Eл\nенергия от дизела" : "Eл\nенергия от\nдизела",
    ],
    body: [
      ["Начало", " ", " ", " ", " ", " "] as RowInput,
      ["Край", " ", " ", " ", " ", " "] as RowInput,
    ],
  });
}

export async function electroPage2(
  printer: ReportPrinter,
  page: PageData,
  garageSettings: GarageSettings
) {
  const printPowerConsumption =
    garageSettings.printPowerConsumption &&
    (!!page.vehicle?.includeEnergy || !!page.includeEnergyInEmpty);
  const shifts = printPowerConsumption ? 2 : 3;
  // const shifts = 3;

  printer.printSpace(printer.pageMarginY / 2);
  let y = printer.currentPageY;

  for (let i = 0; i < shifts; ++i) {
    printer.setPosition(y);
    printShiftTable(printer, printPowerConsumption, i + 1, shifts);
  }

  printer.printSpace(10);

  if (printPowerConsumption) {
    y = printer.currentPageY;
    for (let i = 0; i < shifts; ++i) {
      printer.setPosition(y);
      powerConsumptionTable(printer, i, shifts);
    }
  }

  y = printer.currentPageY;

  for (let i = 0; i < shifts; ++i) {
    printer.setPosition(y);
    printCoursesTable(printer, printPowerConsumption, i, shifts);
  }
}
